import axios from 'axios'
import urlJoin from 'url-join'
import qs from 'query-string'

import { BASE_URL } from '../config'

import extract from './../../function/extract'
import customFetch from './../../function/customFetch'

const url = `${BASE_URL}/projects`

export const fetchDetails = async ({
  id,
  queryParams
}: {
  id: string
  queryParams?: { company_id?: string; show_scrapped?: boolean }
}): Promise<GetProjectDetails> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  try {
    const endpoint = qs.stringifyUrl(
      { url: `${url}/${id}`, query: queryParams },
      { skipNull: true, skipEmptyString: true }
    )

    const response = await customFetch(endpoint, { headers })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: undefined,
      error: error.message
    }
  }
}

export const editDetails = async ({
  id,
  body
}: {
  id: string
  body: Record<string, any>
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const editDraft = async ({
  draft_id,
  body
}: {
  draft_id: string
  body: Record<string, unknown>
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  const draftUrl = `${BASE_URL}/project-drafts/${draft_id}`
  try {
    const response = await fetch(`${draftUrl}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const createProject = async ({
  order_id,
  draft_id,
  body,
  type = 'project'
}: {
  order_id?: string | null
  draft_id?: string | null
  type?: 'project' | 'draft'
  body: Record<string, any>
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }

  let _url = BASE_URL
  switch (type) {
    case 'project':
      _url = `${BASE_URL}/projects`
      break
    case 'draft':
      _url = `${BASE_URL}/project-drafts`
      break
    default:
    //
  }

  try {
    const response = await customFetch(`${_url}`, {
      method: 'POST',
      headers,
      body: order_id // We only have order_id when the account type is SME and not enterprise
        ? JSON.stringify({ order_id, draft_id, ...body })
        : JSON.stringify({ draft_id, ...body })
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }

    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const renameProject = async ({
  projectId,
  body
}: {
  projectId: string
  body: Record<string, unknown>
}): Promise<ApiResponseV2<{ success: string }>> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  const renameUrl = urlJoin(`${BASE_URL}/projects/${projectId}`)
  try {
    if (!projectId) throw new Error('Project id is required')
    const response = await axios.put(renameUrl, body, { headers })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const deleteProject = async ({
  id,
  type
}: {
  id: string
  type: 'draft'
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }

  const _url = `${BASE_URL}/project-drafts`

  try {
    const response = await customFetch(`${_url}/${id}`, {
      method: 'DELETE',
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

// using the same function for fetching list & details of template & draft
export const fetchProjectTemplateList = async ({
  query_params = {},
  page = 0,
  page_size = 10,
  type = 'draft'
}: {
  query_params?: Partial<ProjectQueryParams>
  page?: number
  page_size?: number
  type?: 'draft'
}): Promise<GetProjectTemplateList> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }

  // adding query params
  const reqQueryParams = new URLSearchParams()
  reqQueryParams.append('current_page', page.toString())
  reqQueryParams.append('page_size', page_size.toString())
  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // adding query param only if it has valid value (undefined or null or empty string or [])
    if (queryParam?.toString()) {
      reqQueryParams.append(key, queryParam.toString())
    }
  })

  const _url = `${BASE_URL}/project-drafts`

  try {
    const response = await customFetch(`${_url}?${reqQueryParams.toString()}`, {
      headers
    })

    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: {
          records: result.records,
          total_records: result.total_records
        },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        records: [],
        total_records: 0
      },
      error: error.message
    }
  }
}

export const fetchTemplateDetails = async ({
  id,
  type = 'draft'
}: {
  id: string
  type?: 'draft'
}): Promise<GetProjectTemplateDetails> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }

  const _url = `${BASE_URL}/project-drafts`

  try {
    const response = await customFetch(`${_url}/${id}`, {
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

interface ICreateOrderBody {
  company_id: string
  promo_code?: string
  draft_id?: string | null
  items: {
    type: string
    vertical: number
    title: string
    genre: number
    experience_level: string
    includes: { name: string; word_count: number | null }[]
  }[]
}

export const createOrder = async (
  body: ICreateOrderBody
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }

  try {
    const response = await fetch(`${BASE_URL}/orders`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.description)
    }

    return {
      status: 'ok',
      data: result.data,
      error: undefined
    }
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

interface IVerifyOrderBody {
  vendorPaymentId: string
  verificationSignature: string
  draft_id?: string | null
}

export const verifyOrder = async (
  orderID: string,
  body: IVerifyOrderBody
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }

  try {
    const response = await fetch(`${BASE_URL}/orders/${orderID}/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.description)
    }

    return {
      status: 'ok',
      data: result.data,
      error: undefined
    }
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const createQuotation = async (body: unknown): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await fetch(`${BASE_URL}/estimates`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const acceptAllPendingAssignments = async (
  projectID: string,
  body: { status: AssignmentStatus }
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }

  try {
    const response = await fetch(`${BASE_URL}/projects/${projectID}/status`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })

    const result = await response.json()

    if (response.ok) {
      return { status: 'ok', data: result.data, error: undefined }
    }

    throw new Error(result.description)
  } catch (error) {
    return { status: 'error', data: null, error: error.message }
  }
}

export const addAssignments = async (
  assignmentPayload: unknown
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  const assignmentURL = urlJoin(`${BASE_URL}/assignments/`)

  try {
    const response = await axios.post(assignmentURL, assignmentPayload, {
      headers
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const submitProjectFeedbacks = async (
  projectID: string,
  body: {
    feedback_text: string
    rating: number
    inputs: string[]
    compliments: string[]
    assignment_ids?: string[]
  }
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }

  try {
    const response = await fetch(`${BASE_URL}/projects/${projectID}/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })

    const result = await response.json()

    if (response.ok) {
      return { status: 'ok', data: result.data, error: undefined }
    }

    throw new Error(result.description)
  } catch (error) {
    return { status: 'error', data: null, error: error.message }
  }
}

export const editProjectSettings = async (
  projectID: string,
  body: { collaboration: boolean }
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }

  const URL = urlJoin(BASE_URL, '/projects/', projectID, '/settings')

  try {
    const response = await axios.put(URL, body, { headers })

    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}
